import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/layout/Navbar';
import PageHeader from '../components/layout/PageHeader';
import { SPORTS, quickPicksForSport } from '../data/sports';
import VenueLookup from '../components/VenueLookup';
import '../components/VenueLookup.css';
import {
  Flag, ArrowRight, ArrowLeft, Check, Plus, X, Users, AlertCircle, PartyPopper,
} from 'lucide-react';
import './CreateClubPage.css';

/**
 * Self-serve club creation wizard (onboarding push slice 2, 27 Aug 2026).
 *
 * ① Choose sport → ② Name + club address (compulsory, W-2) → ③ Add teams
 * (sport-appropriate quick-picks + free text) → ④ Terms clickwrap → create.
 * The creator becomes club admin and lands on their new hub, where the admin
 * guided tour starts.
 *
 * The address is picked from the sports-club-biased lookup (W-4) and does
 * two jobs: it's the first thing Marakin verifies, and it becomes the Home
 * Ground (map + weather on home fixtures) with no second entry. Website and
 * the admin's role moved OFF the wizard and onto the "Submit for
 * verification" form in Club Admin → Club Sponsors (29 Aug decision: address
 * compulsory here, the rest funnelled post-onboarding).
 *
 * Community tools are live immediately; sponsorship/QR tools unlock once
 * Marakin verifies the club (clubs.verified_at — the Sponsors tab explains).
 */

const STEPS = ['Sport', 'Name', 'Teams', 'Confirm'];

const CreateClubPage = () => {
  const { user, loading, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [sport, setSport] = useState('');        // canonical sport string sent to the API
  const [customSport, setCustomSport] = useState('');
  const [isOther, setIsOther] = useState(false);
  const [clubName, setClubName] = useState('');
  const [address, setAddress] = useState(null); // picked VenueLookup candidate
  const [selectedTeams, setSelectedTeams] = useState([]); // [{name, ageGroup}]
  const [customTeam, setCustomTeam] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // UC-B5: come back to the wizard after signing in
    if (!loading && !user) navigate('/login', { replace: true, state: { from: '/create-club' } });
  }, [user, loading, navigate]);

  const effectiveSport = isOther ? customSport.trim() : sport;
  const quickPicks = useMemo(() => quickPicksForSport(effectiveSport), [effectiveSport]);

  const slugPreview = clubName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const isSelected = (name) => selectedTeams.some(t => t.name.toLowerCase() === name.toLowerCase());
  const toggleTeam = (pick) => {
    setSelectedTeams(prev =>
      prev.some(t => t.name.toLowerCase() === pick.name.toLowerCase())
        ? prev.filter(t => t.name.toLowerCase() !== pick.name.toLowerCase())
        : prev.length >= 30 ? prev : [...prev, pick]
    );
  };
  const addCustomTeam = () => {
    const name = customTeam.trim();
    if (name.length < 2 || isSelected(name) || selectedTeams.length >= 30) return;
    setSelectedTeams(prev => [...prev, { name, ageGroup: null }]);
    setCustomTeam('');
  };

  const create = useMutation({
    mutationFn: () => axios.post('/clubs', {
      name: clubName.trim(),
      sport: effectiveSport,
      address,
      teams: selectedTeams,
      termsAccepted,
    }).then(r => r.data),
    onSuccess: async (club) => {
      setError(null);
      // Memberships changed — refresh so the hub knows we're its admin,
      // and so the admin guided tour auto-starts (tourCompletedAt null).
      await refreshUser();
      navigate(`/${club.slug}`, { replace: true });
    },
    onError: (e) => setError(e.response?.data?.error || 'Something went wrong — please try again.'),
  });

  const canNext =
    step === 0 ? !!effectiveSport
    : step === 1 ? clubName.trim().length >= 3 && !!address
    : step === 2 ? true // teams optional — they can add later in Club Admin
    : termsAccepted;

  if (loading || !user) return null;

  return (
    <div className="create-club-page">
      <Navbar />
      <PageHeader
        icon={Flag}
        title="Start your club"
        subtitle={<>A few quick steps and your club is live — you'll be its admin.</>}
      />

      <div className="create-club-inner">
        {/* Progress */}
        <div className="cc-progress" aria-label={`Step ${step + 1} of ${STEPS.length}`}>
          {STEPS.map((label, i) => (
            <div key={label} className={`cc-progress-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
              <span className="cc-progress-dot">{i < step ? <Check size={12} /> : i + 1}</span>
              <span className="cc-progress-label">{label}</span>
            </div>
          ))}
        </div>

        <div className="card cc-card">
          {/* ── Step 1: sport ── */}
          {step === 0 && (
            <>
              <h2>What does your club play?</h2>
              <p className="cc-hint">This shapes the team suggestions in a moment — you can run any sport on Marakin.</p>
              <div className="cc-sport-grid">
                {SPORTS.map(s => (
                  <button
                    key={s.key}
                    type="button"
                    className={`cc-sport-chip ${!isOther && sport === s.key ? 'selected' : ''}`}
                    onClick={() => { setSport(s.key); setIsOther(false); }}
                  >
                    <span className="cc-sport-emoji">{s.emoji}</span> {s.label}
                  </button>
                ))}
                <button
                  type="button"
                  className={`cc-sport-chip ${isOther ? 'selected' : ''}`}
                  onClick={() => { setIsOther(true); setSport(''); }}
                >
                  <span className="cc-sport-emoji">✨</span> Other
                </button>
              </div>
              {isOther && (
                <div className="form-group" style={{ marginTop: 14 }}>
                  <label className="label">Your sport</label>
                  <input
                    className="input"
                    value={customSport}
                    onChange={e => setCustomSport(e.target.value)}
                    placeholder="e.g. Lacrosse, Rowing, Athletics"
                    maxLength={100}
                    autoFocus
                  />
                </div>
              )}
            </>
          )}

          {/* ── Step 2: name ── */}
          {step === 1 && (
            <>
              <h2>Name your club</h2>
              <p className="cc-hint">Use the full name your community knows — you can add a nickname and badge later in Club Settings.</p>
              <div className="form-group">
                <label className="label">Club name</label>
                <input
                  className="input"
                  value={clubName}
                  onChange={e => setClubName(e.target.value)}
                  placeholder="e.g. Nailsea & Backwell RFC"
                  maxLength={100}
                  autoFocus
                />
                {slugPreview && (
                  <p className="cc-slug-preview">Your club's web address will be something like <strong>marakin.co.uk/{slugPreview}</strong></p>
                )}
              </div>
              <div className="form-group">
                <label className="label" htmlFor="cc-address">Club address</label>
                <p className="cc-slug-preview" style={{ marginTop: -2, marginBottom: 8 }}>
                  Type the club's name and town, or its postcode, and pick the match. This becomes your
                  home ground — map and weather on every home fixture — and it's the first thing we use
                  to verify your club.
                </p>
                <VenueLookup
                  inputId="cc-address"
                  value={address}
                  onChange={setAddress}
                  placeholder={`e.g. ${clubName.trim() || 'Clevedon RFC'}, or a postcode`}
                />
              </div>
            </>
          )}

          {/* ── Step 3: teams ── */}
          {step === 2 && (
            <>
              <h2>Add your teams</h2>
              <p className="cc-hint">
                Tap the ones your club runs — don't worry about getting it perfect, teams can be
                added, renamed or removed any time in Club Admin.
              </p>
              <div className="cc-team-grid">
                {quickPicks.map(pick => (
                  <button
                    key={pick.name}
                    type="button"
                    className={`cc-team-chip ${isSelected(pick.name) ? 'selected' : ''}`}
                    onClick={() => toggleTeam(pick)}
                  >
                    {isSelected(pick.name) && <Check size={13} />} {pick.name}
                  </button>
                ))}
              </div>
              <div className="cc-custom-team">
                <input
                  className="input"
                  value={customTeam}
                  onChange={e => setCustomTeam(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomTeam(); } }}
                  placeholder="Add another team…"
                  maxLength={100}
                />
                <button type="button" className="btn btn-secondary btn-sm" onClick={addCustomTeam} disabled={customTeam.trim().length < 2}>
                  <Plus size={14} /> Add
                </button>
              </div>
              {selectedTeams.length > 0 && (
                <div className="cc-selected-teams">
                  <p className="cc-selected-head"><Users size={13} /> {selectedTeams.length} team{selectedTeams.length !== 1 ? 's' : ''} selected</p>
                  <div className="cc-selected-list">
                    {selectedTeams.map(t => (
                      <span key={t.name} className="cc-selected-pill">
                        {t.name}
                        <button type="button" aria-label={`Remove ${t.name}`} onClick={() => toggleTeam(t)}><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Step 4: confirm + terms ── */}
          {step === 3 && (
            <>
              <h2>Ready to go</h2>
              <div className="cc-summary">
                <div className="cc-summary-row"><span>Club</span><strong>{clubName.trim()}</strong></div>
                <div className="cc-summary-row"><span>Sport</span><strong>{effectiveSport}</strong></div>
                <div className="cc-summary-row"><span>Home ground</span><strong>{address?.label && address.label !== address.line ? `${address.label} — ${address.line}` : address?.line}</strong></div>
                <div className="cc-summary-row"><span>Teams</span><strong>{selectedTeams.length ? selectedTeams.map(t => t.name).join(', ') : 'None yet — add them later'}</strong></div>
                <div className="cc-summary-row"><span>Your role</span><strong>Club admin</strong></div>
              </div>
              <p className="cc-verify-note">
                Everything your community needs — teams, fixtures, training, news, RSVPs — works
                straight away. <strong>Marakin brings sponsor money into verified clubs</strong>: once
                you're in, Club Admin → Club Sponsors asks for a couple of details so we can verify
                you and switch on your sponsorship tools.
              </p>
              <label className="cc-terms">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={e => setTermsAccepted(e.target.checked)}
                />
                <span>
                  I'm 18 or over and I agree to the Marakin{' '}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link> and{' '}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link> on
                  behalf of my club.
                </span>
              </label>
              {error && <p className="cc-error"><AlertCircle size={14} /> {error}</p>}
            </>
          )}

          {/* ── Nav buttons ── */}
          <div className="cc-actions">
            {step > 0 ? (
              <button type="button" className="btn cc-back" onClick={() => { setError(null); setStep(s => s - 1); }}>
                <ArrowLeft size={15} /> Back
              </button>
            ) : <span />}
            {step < STEPS.length - 1 ? (
              <button type="button" className="btn btn-primary" disabled={!canNext} onClick={() => setStep(s => s + 1)}>
                Next <ArrowRight size={15} />
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                disabled={!termsAccepted || create.isPending}
                onClick={() => create.mutate()}
              >
                <PartyPopper size={15} /> {create.isPending ? 'Creating…' : 'Create my club'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateClubPage;
